import { useEffect, useEffectEvent } from 'react';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { readStorage, writeStorage } from 'utils/storage';

import { useSearchContext } from '@cast/shared-search-ui';

const SHARED_FILTER_KEYS = ['clusterIds', 'namespaces', 'labels'] as const;
type SharedFilterKey = (typeof SHARED_FILTER_KEYS)[number];

const getSessionStorageKey = (orgId = '') =>
  `security-insights-filters-${orgId}`;

type StorageStructure = {
  clusterIds?: string[];
  namespaces?: string[];
  labels?: string[];
};

/**
 * Hook to persist and restore shared filter values from session storage.
 */
export const useSharedFilters = () => {
  const searchCtx = useSearchContext();
  const { currentOrganization } = useOrganizations();

  const loadFilters = useEffectEvent(() => {
    const storage = readStorage<StorageStructure>(
      getSessionStorageKey(currentOrganization?.id),
      {},
      'session'
    );

    const mergedFilters = {
      ...storage,
      ...searchCtx?.values,
    };
    if (Object.keys(mergedFilters).length > 0) {
      searchCtx?.setValues(mergedFilters);
    }
  });

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    if (!searchCtx) {
      return;
    }

    const sharedFilters: Record<string, any> = {};
    Object.entries(searchCtx.values).forEach(([key, value]) => {
      if (SHARED_FILTER_KEYS.includes(key as SharedFilterKey)) {
        sharedFilters[key] = value;
      }
    });

    writeStorage(
      getSessionStorageKey(currentOrganization?.id),
      sharedFilters,
      'session'
    );
  }, [currentOrganization?.id, searchCtx, searchCtx?.values]);
};
